import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  alpha: number;
}

interface ParticleCanvasProps {
  mouseX?: number;
  mouseY?: number;
  particleCount?: number;
  /** Hex color string, e.g. "#00E5FF" */
  color?: string;
  connectionDistance?: number;
  repelRadius?: number;
  speed?: number;
  className?: string;
}

function hexToRgb(hex: string): [number, number, number] {
  const c = hex.replace("#", "");
  return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
}

export default function ParticleCanvas({
  mouseX = -1000,
  mouseY = -1000,
  particleCount = 65,
  color = "#00E5FF",
  connectionDistance = 130,
  repelRadius = 120,
  speed = 0.4,
  className = "",
}: ParticleCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Keep mutable state in a ref so the RAF loop can access latest values
  const stateRef = useRef<{
    particles: Particle[];
    mouseX: number;
    mouseY: number;
    animId: number;
    color: [number, number, number];
    connectionDistance: number;
    repelRadius: number;
  }>({
    particles: [],
    mouseX: -1000,
    mouseY: -1000,
    animId: 0,
    color: hexToRgb(color),
    connectionDistance,
    repelRadius,
  });

  // Sync mouse position to ref on every render cycle (no re-create of RAF loop)
  useEffect(() => {
    stateRef.current.mouseX = mouseX;
    stateRef.current.mouseY = mouseY;
  }, [mouseX, mouseY]);

  // Sync color to ref
  useEffect(() => {
    stateRef.current.color = hexToRgb(color);
    stateRef.current.connectionDistance = connectionDistance;
    stateRef.current.repelRadius = repelRadius;
  }, [color, connectionDistance, repelRadius]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const state = stateRef.current;

    function init() {
      const w = canvas!.offsetWidth || canvas!.width;
      const h = canvas!.offsetHeight || canvas!.height;
      canvas!.width = w;
      canvas!.height = h;
      state.particles = Array.from({ length: particleCount }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        r: Math.random() * 1.5 + 1,
        alpha: Math.random() * 0.35 + 0.15,
      }));
    }

    function draw() {
      const w = canvas!.width;
      const h = canvas!.height;
      ctx!.clearRect(0, 0, w, h);

      const { mouseX: mx, mouseY: my, particles, color: [r, g, b], connectionDistance: cd, repelRadius: rr } = state;

      for (const p of particles) {
        // Mouse repulsion
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < rr && dist > 0) {
          const force = ((rr - dist) / rr) * 0.11;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }

        // Velocity damping
        p.vx *= 0.98;
        p.vy *= 0.98;
        p.x += p.vx;
        p.y += p.vy;

        // Boundary bounce
        if (p.x < 0) { p.x = 0; p.vx = Math.abs(p.vx) * 0.8; }
        if (p.x > w) { p.x = w; p.vx = -Math.abs(p.vx) * 0.8; }
        if (p.y < 0) { p.y = 0; p.vy = Math.abs(p.vy) * 0.8; }
        if (p.y > h) { p.y = h; p.vy = -Math.abs(p.vy) * 0.8; }

        // Draw particle
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(${r},${g},${b},${p.alpha})`;
        ctx!.fill();
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < cd) {
            const alpha = (1 - d / cd) * 0.1;
            ctx!.beginPath();
            ctx!.moveTo(particles[i].x, particles[i].y);
            ctx!.lineTo(particles[j].x, particles[j].y);
            ctx!.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
            ctx!.lineWidth = 0.6;
            ctx!.stroke();
          }
        }
      }

      state.animId = requestAnimationFrame(draw);
    }

    init();
    draw();

    const observer = new ResizeObserver(() => init());
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(state.animId);
      observer.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [particleCount, speed]);

  return <canvas ref={canvasRef} className={`w-full h-full block ${className}`} />;
}
